import {useEffect, useState, ReactNode, FC} from "react"
import {useSearchParams} from "react-router-dom"
import {useTranslation} from "react-i18next"

import {useCustomerInfo} from "./useCustomerInfo"
import {isValidLocale} from "./Locales"

import {LoadingPage} from "@indebted/components/Loading"

const LocaleLoader: FC<{children: ReactNode}> = ({children}) => {
	const [searchParams] = useSearchParams()
	const overrideLocale = searchParams.get("locale")

	const {i18n} = useTranslation()

	const {customerInfo, isLoading, error} = useCustomerInfo()

	const [localeError, setLocaleError] = useState<string | null>(null)

	const [locale, setLocale] = useState<string | null>(null)

	useEffect(() => {
		const changeLocale = async (locale: string) => {
			if (!isValidLocale(locale)) {
				console.error("Invalid locale", locale)
				setLocaleError(`Invalid locale: ${locale}`)
				return
			}

			await i18n.changeLanguage(locale, (err) => {
				if (err) {
					console.error("Error changing locale", err)
					setLocaleError(err)
				} else {
					setLocale(locale)
				}
			})
		}

		if (overrideLocale) {
			changeLocale(overrideLocale)
			return
		}

		if (!customerInfo) {
			return
		}

		if (customerInfo.Locale) {
			// Change i18n language to the fetched locale
			changeLocale(customerInfo.Locale)
		} else {
			console.error("No locale found")
			setLocaleError("No locale found")
		}
	}, [customerInfo, overrideLocale, i18n])

	useEffect(() => {
		if (locale) {
			document.documentElement.lang = locale
			document.documentElement.dir = i18n.dir(locale)
			document.body.setAttribute("data-locale", locale)
			document.body.setAttribute("dir", i18n.dir(locale))
		}
	}, [locale, i18n])

	if (error || localeError) {
		// Show children for now to avoid breaking changes
		return <>{children}</>
	}

	// Show loading screen while fetching locale
	if (isLoading) {
		return <LoadingPage />
	}

	if (!customerInfo && !locale) {
		console.error("No locale found and no request to fetch locale", window.location.pathname)
		return <>{children}</>
	}

	return <>{children}</>
}

export {LocaleLoader}
